// Редовият модел на справка „По станции".
//
// Един ред = една станция за целия период. Числата идват от ДВА ендпойнта:
// оборотът и сметките от `GET /reports/analytics/stations`, сторното от
// `GET /bills/cancellations`. Затова ключът на реда е внимателен въпрос:
// `StationReportSummary` НЯМА id, носи само име, докато сторното е ключовано
// по `station_id`. Редът се ключова по ИМЕ, а id-то се разрешава от
// разбивката по дни на същия отговор (тя носи и двете) и от менюто.
//
// Разбивката по плащане е КАТО в „По сервитьори" и „Продажби по периоди":
// смесеното няма свой метод, а е разпределено в „В брой" и „С карта". Затова
// тези два метода идват от `totalCash`/`totalCard` (BE ги разпределя
// пропорционално), а останалите — от `methods[]`, където смесеното стои цяло и
// тук се прескача. Сборът на методите е оборотът.

import { parseDecimal } from "../../../../components/dataTable";
import type { CancelledItem } from "../../../../api/reportsCancellations";
import type { StationSummaryItem } from "../../../../api/reportsStations";
import { deltaPercent } from "../reportDelta";

// Bill.payment_method_id на системните методи (BE: PAYMENT_METHOD_*).
const CASH_PAYMENT_METHOD_ID = 1;
const CARD_PAYMENT_METHOD_ID = 2;
export const MIXED_PAYMENT_METHOD_ID = 3;

export interface StationRow {
  /** Ключът на реда — BE връща обобщението само с име. */
  stationName: string;
  /** Разрешено id; `null`, когато станцията я няма нито в дните, нито в менюто. */
  stationId: number | null;
  revenue: number;
  /** Сметки, докоснали станцията. */
  bills: number;
  /** Дял от оборота на периода в проценти. */
  share: number;
  voidTotal: number;
  voidRows: number;
  /** Δ на оборота спрямо предходния период; `null` = няма с какво да се сравни. */
  delta: number | null;
  /**
   * Сума по метод на плащане със смесеното РАЗПРЕДЕЛЕНО в „В брой" и
   * „С карта" — колоните по метод, лентата и Excel.
   */
  methodAmounts: Map<number, number>;
  /**
   * Брой сметки по метод — само за `count` на лентата по методи; колона за
   * него нарочно няма. Смесената сметка се брои и при „В брой", и при „С карта".
   */
  methodCounts: Map<number, number>;
}

export interface StationsTotals {
  revenue: number;
  bills: number;
  voidTotal: number;
  voidRows: number;
  stations: number;
}

export interface StationVoids {
  total: number;
  rows: number;
}

/**
 * Сметките, докоснали станцията.
 *
 * Броим по `methods[]`, а НЕ по `countCash + countCard + countMixed`: методите
 * отвъд „в брой" и „с карта" („По сметка", персонализираните) не влизат в
 * трите брояча на BE-то и станция, платена само така, излизаше с нула сметки
 * при ненулев оборот. Трите брояча остават резервен път за отговор без
 * разбивка по методи.
 */
const billsOf = (item: StationSummaryItem): number => {
  if (item.methods?.length) {
    return item.methods.reduce(
      (sum, method) => sum + parseDecimal(method.count),
      0,
    );
  }
  return (
    parseDecimal(item.countCash) +
    parseDecimal(item.countCard) +
    parseDecimal(item.countMixed)
  );
};

/** Добавя сума и брой сметки към метода на реда. */
const addToMethod = (
  row: StationRow,
  methodId: number,
  amount: number,
  count: number,
) => {
  row.methodAmounts.set(methodId, (row.methodAmounts.get(methodId) ?? 0) + amount);
  row.methodCounts.set(methodId, (row.methodCounts.get(methodId) ?? 0) + count);
};

/**
 * Методите на един запис от обобщението. Кешът и картата идват наготово със
 * смесеното вътре (`totalCash`/`totalCard`); смесеното само по себе си метод
 * няма, затова 1/2/3 от `methods[]` се прескачат — иначе същите пари се
 * броят два пъти.
 */
const addMethodsOf = (row: StationRow, item: StationSummaryItem) => {
  for (const method of item.methods ?? []) {
    const id = method.payment_method_id;
    if (
      id === CASH_PAYMENT_METHOD_ID ||
      id === CARD_PAYMENT_METHOD_ID ||
      id === MIXED_PAYMENT_METHOD_ID
    ) {
      continue;
    }
    addToMethod(row, id, parseDecimal(method.amount), parseDecimal(method.count));
  }
  const cash = parseDecimal(item.totalCash);
  const card = parseDecimal(item.totalCard);
  const cashCount = parseDecimal(item.countCash);
  const cardCount = parseDecimal(item.countCard);
  const mixedCount = parseDecimal(item.countMixed);
  if (cash || cashCount || mixedCount) {
    addToMethod(row, CASH_PAYMENT_METHOD_ID, cash, cashCount + mixedCount);
  }
  if (card || cardCount || mixedCount) {
    addToMethod(row, CARD_PAYMENT_METHOD_ID, card, cardCount + mixedCount);
  }
};

/** Оборотът на целия период — знаменателят на дяловете и на Δ кутийката. */
export const sumRevenue = (summary: StationSummaryItem[]): number =>
  summary.reduce((sum, item) => sum + parseDecimal(item.total), 0);

/** Сторното за периода, без разбивка по станции — за кутийката и нейната Δ. */
export const sumVoids = (items: CancelledItem[]): StationVoids => ({
  total: items.reduce((sum, item) => sum + parseDecimal(item.total_sum), 0),
  rows: items.length,
});

export const voidsByStation = (
  items: CancelledItem[],
): Map<number, StationVoids> => {
  const byStation = new Map<number, StationVoids>();
  for (const item of items) {
    const current = byStation.get(item.station_id) ?? { total: 0, rows: 0 };
    current.total += parseDecimal(item.total_sum);
    current.rows += 1;
    byStation.set(item.station_id, current);
  }
  return byStation;
};

interface BuildInput {
  summary: StationSummaryItem[];
  /** `null` = сравнението е изключено; тогава колоната Δ не се рисува. */
  previousSummary: StationSummaryItem[] | null;
  voidsById: Map<number, StationVoids>;
  nameById: Map<number, string>;
  idByName: Map<string, number>;
}

export const buildStationRows = ({
  summary,
  previousSummary,
  voidsById,
  nameById,
  idByName,
}: BuildInput): { rows: StationRow[]; totals: StationsTotals } => {
  const rowByName = new Map<string, StationRow>();

  const ensureRow = (stationName: string, stationId: number | null) => {
    const existing = rowByName.get(stationName);
    if (existing) {
      if (existing.stationId === null) existing.stationId = stationId;
      return existing;
    }
    const created: StationRow = {
      stationName,
      stationId,
      revenue: 0,
      bills: 0,
      share: 0,
      voidTotal: 0,
      voidRows: 0,
      delta: null,
      methodAmounts: new Map(),
      methodCounts: new Map(),
    };
    rowByName.set(stationName, created);
    return created;
  };

  for (const item of summary) {
    const row = ensureRow(
      item.stationName,
      idByName.get(item.stationName) ?? null,
    );
    row.revenue += parseDecimal(item.total);
    row.bills += billsOf(item);
    addMethodsOf(row, item);
  }

  // Станция, на която ВСИЧКО маркирано е сторнирано, я няма в обобщението, но
  // е факт за периода. Влиза с нулев оборот — иначе кутийката „Сторно по
  // станции" сумира пари, които не се виждат в нито един ред.
  for (const [stationId, voids] of voidsById) {
    const stationName = nameById.get(stationId) ?? `#${stationId}`;
    const row = ensureRow(stationName, stationId);
    row.voidTotal += voids.total;
    row.voidRows += voids.rows;
  }

  const previousByName = new Map<string, number>();
  for (const item of previousSummary ?? []) {
    previousByName.set(
      item.stationName,
      (previousByName.get(item.stationName) ?? 0) + parseDecimal(item.total),
    );
  }

  const rows = [...rowByName.values()];
  const totals: StationsTotals = {
    revenue: rows.reduce((sum, row) => sum + row.revenue, 0),
    bills: rows.reduce((sum, row) => sum + row.bills, 0),
    voidTotal: rows.reduce((sum, row) => sum + row.voidTotal, 0),
    voidRows: rows.reduce((sum, row) => sum + row.voidRows, 0),
    stations: rows.length,
  };

  for (const row of rows) {
    row.share = totals.revenue > 0 ? (row.revenue / totals.revenue) * 100 : 0;
    // Съпоставянето е по име: обобщението на предходния период също носи само
    // име. `deltaPercent` връща `null`, когато преди е било нула, а сега не е —
    // ръст от нищо не е „+100 %", а нова станция.
    row.delta = previousSummary
      ? deltaPercent(row.revenue, previousByName.get(row.stationName) ?? 0)
      : null;
  }

  rows.sort((a, b) => b.revenue - a.revenue);
  return { rows, totals };
};
